"use client";

import type { ReactNode } from "react";
import { createContext, useContext, useEffect } from "react";
import type {
    APIAppEnv,
    APIUser,
    APIIncompleteUser,
    APIServerEnv } from "@rosette/ts/src/business/api/api_schema.js";
import {
    toCompleteUser,
    toIncompleteUser,
} from "@rosette/ts/src/business/api/api_schema.js";
import { Rune } from "@rosette/ts/src/business/Rune.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import { Optional } from "@rosette/ts/src/util/Optional.js";
import { matomo } from "@/components/Matomo.js";

// The context allows useEnv to work straight away within the context (no useEffect delay).
// The holder allows useEnv to work outside the context (with useEffect delay).
const EnvContext = createContext<APIAppEnv | null>(null);
const theEnvHolder = new Rune<Optional<APIAppEnv>>(Optional.empty());

export function useOptionalAppEnv(): APIAppEnv | null {
    const ctxEnv = useContext(EnvContext);
    const holderEnv = useRune(theEnvHolder).getOrNull();
    return ctxEnv ?? holderEnv;
}

export function useAppEnv(): APIAppEnv {
    const env = useOptionalAppEnv();
    if (!env)
        throw new Error("No env available!");

    return env;
}

export function useOptionalEnv(): APIServerEnv | null {
    const env = useOptionalAppEnv();
    return env ? env.env : null;
}

export function useEnv(): APIServerEnv {
    const env = useOptionalEnv();
    if (!env)
        throw new Error("No user available!");

    return env;
}

export function useOptionalUser(): APIUser | null {
    const appEnv = useOptionalAppEnv();
    return appEnv ? toCompleteUser(appEnv.user) : null;
}

export function useOptionalIncompleteUser(): APIIncompleteUser | null {
    const appEnv = useOptionalAppEnv();
    return appEnv ? toIncompleteUser(appEnv.user) : null;
}

export function useUserOrIncompleteUser(): {
    user: APIUser | null;
    incompleteUser: APIIncompleteUser | null;
} {
    const incompleteUser = useOptionalIncompleteUser();
    const user = useOptionalUser();
    return { incompleteUser, user };
}

export function useUser(): APIUser {
    const user = useOptionalUser();
    if (!user)
        throw new Error("No user available!");

    return user;
}

interface EnvProviderProps {
    children: ReactNode;
    appEnv: APIAppEnv;
}

export function EnvProvider({ children, appEnv }: EnvProviderProps) {
    const existingEnv = theEnvHolder.get().getOrNull();

    // Record user with matomo
    const completeUser = toCompleteUser(appEnv.user);
    matomo.recordUser(completeUser);

    useEffect(() => {
        if (existingEnv !== appEnv) {
            theEnvHolder.set(Optional.of(appEnv));
        }
    }, [existingEnv, appEnv]);

    return (
        <EnvContext.Provider value={appEnv}>
            {children}
        </EnvContext.Provider>
    );
}
